import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

import { Alert, Box, Button, Container, FormField, Select, SpaceBetween } from '@amzn/awsui-components-react';

import { useAppSelector } from '../../app/hooks';
import DrishtiPageHeader from '../../components/drishtiPageHeader';
import DrishtiStationFacts from '../../components/drishtiStationFacts';
import {
    formatStation,
    isCompleteStationConfig,
    isStationNumber,
    STATION_NUMBER_LENGTH,
    toStationConfig,
} from '../../constants/stationConfig';
import { isListDrishtiStationsResponse, listDrishtiStations, resolveStation } from '../../services/drishtiHandler';
import { EventName, EventType, logDrishtiMetricEvent } from '../../services/eventService';
import {
    clearDrishtiInstall,
    clearDrishtiStationConfig,
    updateDrishtiStationConfig,
    updateDrishtiStationNumber,
} from '../../slice/drishtiInstallConfigSlice';

const NOT_ON_RECORD = 'This workstation is not on record.';
const NOT_LISTED = 'NOT_LISTED';
const DETAILS_UNAVAILABLE = 'Could not get the station details. Check your connection and try again.';
const STATIONS_UNAVAILABLE = 'Station list: could not load the stations for this site. Check your connection.';
const NO_STATIONS = 'No stations are on record for this site.';

const toOption = (stationNumber: string) => ({ label: stationNumber, value: stationNumber });

const DrishtiStation = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const config = useAppSelector((state) => state.drishtiInstallConfig);
    const site = config.site;
    const stationNumber = config.stationNumber ?? '';
    const installStarted = config.installStarted;

    const [stationNumbers, setStationNumbers] = useState<readonly string[] | null>(null);
    const [listFailed, setListFailed] = useState(false);
    const [listAttempt, setListAttempt] = useState(0);
    const [filteringText, setFilteringText] = useState('');
    const [resolving, setResolving] = useState(false);
    const [resolveError, setResolveError] = useState('');

    const logEvent = (eventName: EventName, eventType: EventType, miscEventData?: {}) => {
        logDrishtiMetricEvent({ eventName, eventType, deviceId: config.deviceSerial ?? undefined, miscEventData });
    };

    useEffect(() => {
        if (!site) {
            return;
        }
        setStationNumbers(null);
        setListFailed(false);
        listDrishtiStations(site, (success, result) => {
            if (success && isListDrishtiStationsResponse(result)) {
                setStationNumbers(result.stationNumbers);
                if (stationNumber && !installStarted && !result.stationNumbers.includes(stationNumber)) {
                    dispatch(updateDrishtiStationNumber(''));
                    setResolveError(`Station ${site}-${stationNumber} is no longer on this site's list.`);
                    logEvent(EventName.DRISHTI_STATION_SELECTED, EventType.FAILURE, {
                        site,
                        stationNumber,
                        error: NOT_LISTED,
                    });
                }
                logEvent(EventName.DRISHTI_STATIONS_LISTED, EventType.SUCCESS, {
                    site,
                    stationCount: result.stationNumbers.length,
                });
            } else {
                setListFailed(true);
                logEvent(EventName.DRISHTI_STATIONS_LISTED, EventType.FAILURE, { site, error: result });
            }
        });
    }, [site, listAttempt]);

    if (!site) {
        return <Navigate to="/drishti/site" replace={true} />;
    }

    const station = isStationNumber(stationNumber) ? { site, stationNumber } : null;
    const resolvedStationConfig = isCompleteStationConfig(config.stationConfig) ? config.stationConfig : null;
    const options = (stationNumbers ?? []).filter((number) => number.startsWith(filteringText)).map(toOption);

    const onStationNumberChange = (value?: string) => {
        if (!value) {
            return;
        }
        dispatch(updateDrishtiStationNumber(value));
        setResolveError('');
    };

    const onContinue = () => {
        dispatch(clearDrishtiInstall());
        navigate('/drishti/config');
    };

    const onResolve = () => {
        if (!station) {
            return;
        }
        dispatch(clearDrishtiStationConfig());
        setResolving(true);
        setResolveError('');
        resolveStation(station, (success, result) => {
            setResolving(false);
            const resolvedConfig = success ? toStationConfig(result) : null;
            if (resolvedConfig) {
                dispatch(updateDrishtiStationConfig(resolvedConfig));
                logEvent(EventName.DRISHTI_STATION_SELECTED, EventType.SUCCESS, station);
            } else {
                // An unmapped station is a 200 with an empty body: the shared gateway offers only 200
                // and 400, so "no record" cannot have a status of its own. A 400 (bad input, or the
                // feature flag off) and a gateway 5xx are both ours to fix, not a station to escalate.
                setResolveError(success ? NOT_ON_RECORD : DETAILS_UNAVAILABLE);
                logEvent(EventName.DRISHTI_STATION_SELECTED, EventType.FAILURE, { ...station, error: result });
            }
        });
    };

    return (
        <Container>
            <DrishtiPageHeader
                title={`Select your station at ${site}`}
                description="Choose the station from the list, then check the station details before you continue."
            />
            <SpaceBetween size="l">
                <FormField
                    label="Station number"
                    description={`The ${STATION_NUMBER_LENGTH}-digit number posted at the station.`}
                >
                    <Select
                        placeholder="Choose a station"
                        filteringType="manual"
                        filteringAriaLabel="Filter station numbers"
                        selectedOption={stationNumber ? toOption(stationNumber) : null}
                        options={options}
                        statusType={stationNumbers === null && !listFailed ? 'loading' : 'finished'}
                        loadingText="Loading stations"
                        empty={stationNumbers?.length === 0 ? NO_STATIONS : undefined}
                        disabled={resolving || listFailed}
                        onLoadItems={(e) => setFilteringText(e.detail.filteringText)}
                        onChange={(e) => onStationNumberChange(e.detail.selectedOption.value)}
                    />
                </FormField>

                {listFailed ? (
                    <Alert
                        type="error"
                        action={<Button onClick={() => setListAttempt((attempt) => attempt + 1)}>Retry</Button>}
                    >
                        {STATIONS_UNAVAILABLE}
                    </Alert>
                ) : null}

                {resolveError ? <Alert type="error">{resolveError}</Alert> : null}

                {resolvedStationConfig && station ? (
                    <Alert type="success" header={`Station ${formatStation(station)} resolved`}>
                        <Box margin={{ top: 's' }}>
                            <DrishtiStationFacts config={resolvedStationConfig} />
                        </Box>
                    </Alert>
                ) : null}

                {resolvedStationConfig ? (
                    <Button variant="primary" onClick={onContinue}>
                        Continue
                    </Button>
                ) : (
                    <Button onClick={onResolve} loading={resolving} disabled={!station}>
                        Resolve station
                    </Button>
                )}
            </SpaceBetween>
        </Container>
    );
};

export default DrishtiStation;
